import React, { useContext, useEffect, useRef, useState } from "react";
import { AttributeContext } from "../AttributeContext";
import { getAbsConstraintsByAttributeID, getCFConstraintsByAttributeID } from "api/Attributes";
import { TABSConstraints, TABSConstraintsWithoutValues, TABSValueConstriant, TCFConstraints, TCFConstraintsWithoutValues, TCFValueConstriant } from "../types";
import EditABS from "./EditABS";
import { ABS_SELECTION } from "../AttributeConstants";
import EditCF from "./EditCF";
import Accordion from "Components/DesignSystem/Accordion/Accordion";
import Alert from "Components/DesignSystem/Alert/Alert";
import Spinner from "Components/DesignSystem/Spinner/Spinner";
import Skeleton from "Components/DesignSystem/Skeleton/Skeleton";

type ABSProps = {
  finalABSValue: TABSConstraints,
  finalCFValue: TCFConstraints,
  setFinalABSValue: React.Dispatch<React.SetStateAction<TABSConstraints|null>>,
  setFinalCFValue: React.Dispatch<React.SetStateAction<TCFConstraints|null>>,
  attributeValueCount: number,
  title: string,
  id: string
}

export default function ABS({ finalABSValue, setFinalABSValue, finalCFValue, setFinalCFValue, attributeValueCount, title, id}: ABSProps) {
  const { editingItem } = useContext(AttributeContext);
  const [loading, setLoading] = useState<{status?: string, message?: string}>({ status: "", message: "", });
  /*
    § 288 — the SEARCH half of `loading`. One status covers the search fetch AND the
    view-more append, and a field that spun when the user clicked "View More" would be
    claiming a search nobody asked for. `skip === undefined` is the discriminator both
    fetchers already take. Presentational: it is passed down for the field's spinner
    and read nowhere else.
  */
  const [searchLoading, setSearchLoading] = useState(false);
  const [absDetails, setAbsDetails] = useState<TABSConstraintsWithoutValues|null>(null);
  const [absDetailsCopy, setAbsDetailsCopy] = useState<TABSConstraintsWithoutValues|null>(null);

  const [cfDetails, setCfDetails] = useState<TCFConstraintsWithoutValues|null>(null);
  const [cfDetailsCopy, setCfDetailsCopy] = useState<TCFConstraintsWithoutValues|null>(null);

  const [hasClickedCFToggle, setHasClickedCFToggle] = useState<boolean>(false);
  const [hasClickedABSToggle, setHasClickedABSToggle] = useState<boolean>(false);

  const [valueConstraints, setValueConstraints] = useState<TABSValueConstriant[]>([]);
  const [valueCfConstraints, setValueCfConstraints] = useState<TCFValueConstriant[]>([]);

  const [attributeValuesCount, setAttributeValuesCount] = useState(attributeValueCount ?? 0);
  const [collapse, setCollapse] = useState(false);
  const [disableButtons, setDisableButtons] = useState(true);
  const [search, setSearch] = useState<string|undefined>(undefined);
  const [cfSearch, setCfSearch] = useState<string|undefined>(undefined);
  const initialLoading = useRef<boolean>(true);

  useEffect(() => {
    setAttributeValuesCount(attributeValueCount)
  }, [attributeValueCount]);

  useEffect(() => {
    setLoading({ status: "", message: "", });
    setAbsDetails(null);
    setAttributeValuesCount(0);
    setSearch(undefined);
    setCfSearch(undefined);
    setFinalABSValue(null);
    setFinalCFValue(null);
  }, [editingItem?.id]);

  useEffect(() => {
    if (JSON.stringify(absDetails) === JSON.stringify(absDetailsCopy)) {
      setDisableButtons(true);
    } else if (
      valueConstraints &&
      valueConstraints.length > 0
    ) {
      let hasValidUrl = valueConstraints.some(function (val) {
        return false;
      });
      if (hasValidUrl) {
        setDisableButtons(true);
      } else {
        setDisableButtons(false);
      }
    } else {
      setDisableButtons(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [absDetails]);

  useEffect(() => {
    if(!absDetails) return

    setFinalABSValue((prev) =>  {
      const changedValues = valueConstraints.filter(value => value.changed).map((value) => value.value)

      const nextValueConstraints = [
        ...(prev?.value_constraints.filter(value => !changedValues.includes(value.value)) ?? []), 
        ...valueConstraints.filter(value => value.changed)
      ]

      return { ...absDetails,  value_constraints: nextValueConstraints}
    });
  }, [absDetails, valueConstraints]);


  useEffect(() => {
    if(!cfDetails) return;

    setFinalCFValue((prev) => {
      const changedValues = valueCfConstraints
          .filter(value => value.changed)
          .map(value => value.value);
  
      const nextValueConstraints = [
          ...(prev?.value_constraints.filter(value => !changedValues.includes(value.value)) ?? []),
          ...valueCfConstraints.filter(value => value.changed)
      ];
  
      return {  ...cfDetails, value_constraints: nextValueConstraints 
      };
  });
  
  
  }, [cfDetails, valueCfConstraints]);

  useEffect(() => {
    if (title === ABS_SELECTION) {
      if (collapse && absDetails === null) 
        fetchAbsConstraints()
        .then(() => initialLoading.current = false);
    } else {
      if (collapse && absDetails === null) 
        fetchCfConstraints()
        .then(() => initialLoading.current = false);
    }
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapse]);

  async function fetchAbsConstraints(search?: string, skip?: number, reset?: boolean) {
    if(!editingItem) return setLoading({ status: "Error", message: "Unexpected error" });
    setLoading({ status: "Loading", message: "Loading ABS Details" });
    if (skip === undefined) setSearchLoading(true);

    getAbsConstraintsByAttributeID(editingItem.id, 10, skip, search)
    .then(({constraints, count}) => {
      setLoading({ status: "Success", message: undefined });
      setSearchLoading(false);
      const {value_constraints, ...rest} = constraints;

      if(initialLoading.current || !absDetails) {
        if (rest.abs_enabled == null) rest.abs_enabled = false
        setAbsDetails(rest as TABSConstraintsWithoutValues);
        setAbsDetailsCopy(rest as TABSConstraintsWithoutValues);
      }
      if(value_constraints) setValueConstraints((prev) => {
        const newValueConstraints = [...value_constraints]
        if(finalABSValue?.abs_enabled !== absDetailsCopy?.abs_enabled || hasClickedABSToggle){
          newValueConstraints.forEach((value) => {
            value.abs_show = true
          })
        }

        return prev?.length > 0 && !reset ? [...prev, ...newValueConstraints] : newValueConstraints
      })
      setAttributeValuesCount(count)
      setDisableButtons(true);
    })
    .catch((err) => {
      setLoading({ status: "Error", message: "Error while loading details" });
      setSearchLoading(false);
    })
  }

  async function fetchCfConstraints(search?: string, skip?: number, reset?: boolean) {
    if(!editingItem) return setLoading({ status: "Error", message: "Unexpected error" });
    setLoading({ status: "Loading", message: "Loading CF Details" });
    if (skip === undefined) setSearchLoading(true);

    getCFConstraintsByAttributeID(editingItem.id, 10, skip, search)
    .then(({constraints, count}) => {
      setLoading({ status: "Success", message: undefined });
      setSearchLoading(false);
      const {value_constraints, ...rest} = constraints;

      if(initialLoading.current || !cfDetails) {
        if (rest.cf_enabled == null) rest.cf_enabled = false
        setCfDetails(rest as TCFConstraintsWithoutValues);
        setCfDetailsCopy(rest as TCFConstraintsWithoutValues);
      }
      if(value_constraints) setValueCfConstraints((prev) => {
        const newValueConstraints = [...value_constraints]
        if(finalCFValue?.cf_enabled !== cfDetailsCopy?.cf_enabled || hasClickedCFToggle){
          newValueConstraints.forEach((value) => {
            value.cf_show = finalCFValue?.cf_enabled
          })
        }

        return prev?.length > 0 && !reset ? [...prev, ...newValueConstraints] : newValueConstraints
      })
      setAttributeValuesCount(count)
      setDisableButtons(true);
    })
    .catch((err) => {
      setLoading({ status: "Error", message: "Error while loading details" });
      setSearchLoading(false);
    })
  }


  useEffect(() => {
    if(initialLoading.current) return
    if (title === ABS_SELECTION) {
      fetchAbsConstraints(search, undefined, true);
    } else {
      fetchCfConstraints(cfSearch, undefined, true);
    }
  }, [search])

  function handle_collapse() {
    setCollapse(!collapse);
  }

  const hasDetails = title === ABS_SELECTION ? !!absDetails : !!cfDetails;

  return (
    <Accordion
      title={title}
      open={collapse}
      onOpenChange={() => handle_collapse()}
      data-testid="componets-abs-div-1"
      variant="boxed"
    >
      <div className="tw-pb-4">
        <LoadingSuccessErrorAlert loading={loading} hasDetails={hasDetails} />
        {(loading.status === "Success" || loading.status === "Error" ||
          (loading.status === "Loading" && hasDetails)) ?
          title === ABS_SELECTION ? (
            <div className="tw-border tw-border-edge tw-rounded-ctl tw-p-4">
              {absDetails && (
                <EditABS
                  absDetails={absDetails}
                  setAbsDetails={setAbsDetails}
                  attributeValuesCount={attributeValuesCount}
                  fetchAbsConstraints={fetchAbsConstraints}
                  valueConstraints ={valueConstraints}
                  setValueConstraints ={setValueConstraints}
                  search = {search}
                  setSearch = {setSearch}
                  hasClickedABSToggle={hasClickedABSToggle}
                  setHasClickedABSToggle={setHasClickedABSToggle}
                  loading={loading}
                  searchLoading={searchLoading}
                />
              )}
            </div>
          ) : (
            <div className="tw-border tw-border-edge tw-rounded-ctl tw-p-4">
              {cfDetails && (
                <EditCF
                  cfDetails={cfDetails}
                  setCfDetails={setCfDetails}
                  attributeValuesCount={attributeValuesCount}
                  fetchCfConstraints={fetchCfConstraints}
                  valueCfConstraints ={valueCfConstraints}
                  setValueCfConstraints ={setValueCfConstraints}
                  search = {cfSearch}
                  setSearch = {setCfSearch}
                  hasClickedCFToggle={hasClickedCFToggle}
                  setHasClickedCFToggle={setHasClickedCFToggle}
                  loading={loading}
                  searchLoading={searchLoading}
                />
              )}
            </div>
          ) : null}
      </div>
    </Accordion>
  );
}

/* Module loading grammar (user-requested 2026-09-01): a load with nothing on
   screen renders skeleton form rows — while details are already visible (a
   view-more append) the Edit form stays mounted and shows its own inline
   spinner, and nothing renders here. Alert strips remain for Saving/Error/
   Success (§ 99 roles). */
function LoadingSuccessErrorAlert({ loading, hasDetails }: {loading: {status?: string, message?: string}, hasDetails: boolean}) {
  return (
    <>
      {loading.status === "Saving" && (
        <Alert tone="info" live data-testid="attribute-abs-status">
          <span className="tw-inline-flex tw-items-center tw-gap-2">
            <Spinner size="sm" aria-hidden="true" />
            <span>{loading.message}</span>
          </span>
        </Alert>
      )}
      {loading.status === "Loading" && !hasDetails && (
        <div className="tw-space-y-3" data-testid="attribute-abs-skeleton">
          <Skeleton variant="rect" width="30%" height="24px" />
          <Skeleton variant="rect" width="100%" height="28px" />
          <Skeleton variant="rect" width="100%" height="28px" />
          <Skeleton variant="rect" width="60%" height="28px" />
        </div>
      )}
      {loading.status === "Error" && (
        <Alert tone="danger" live data-testid="attribute-abs-status">
          {loading.message}
        </Alert>
      )}
      {loading.status === "Success" && loading.message !== undefined && (
        <Alert tone="success" live data-testid="attribute-abs-status">
          {loading.message}
        </Alert>
      )}
    </>
  );
}
